import React from 'react';
import { ResponsiveDataList, type Column } from '@shared/ui/adaptive';

import { Trash2 } from 'lucide-react';

export interface RedirectRule {
  id: string;
  sourcePath: string;
  targetUrl: string;
  statusCode: number;
}

export interface RedirectRulesListProps {
  rules: RedirectRule[];
  onDelete?: (ruleId: string) => void;
}

// RedirectRulesList renders 301/302 SEO redirects via ResponsiveDataList (M-I3).
export function RedirectRulesList({ rules, onDelete }: RedirectRulesListProps): React.JSX.Element {
  const columns: Column<RedirectRule>[] = [
    {
      key: 'source',
      header: 'Source Path',
      render: (r: RedirectRule) => <span className="font-mono text-xs text-foreground">{r.sourcePath}</span>,
      width: '30%',
    },
    {
      key: 'status',
      header: 'Code',
      render: (r: RedirectRule) => (
        <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-muted text-muted-foreground">
          {r.statusCode}
        </span>
      ),
      width: '15%',
    },
    {
      key: 'target',
      header: 'Target URL',
      render: (r: RedirectRule) => <span className="font-mono text-xs text-muted-foreground">{r.targetUrl}</span>,
    },
  ];

  if (onDelete) {
    columns.push({
      key: 'actions',
      header: '',
      render: (r: RedirectRule) => (
        <button
          type="button"
          onClick={() => onDelete(r.id)}
          className="p-1.5 text-muted-foreground hover:text-destructive rounded transition-colors"
          title="Delete Redirect"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
      width: '10%',
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">URL Path Redirects (301 / 302)</h2>
        <span className="text-xs text-muted-foreground">{rules.length} active redirects</span>
      </div>
      <ResponsiveDataList<RedirectRule>
        data={rules}
        keyExtractor={(r: RedirectRule) => r.id}
        columns={columns}
        emptyMessage="No URL redirect rules configured."
      />
    </div>
  );
}
